import React, { useState, useEffect, useCallback } from 'react';
import styled, { css, keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
`;

const AlertContainer = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}` || '12px 16px'};
  border-radius: ${({ theme }) => theme.borderRadius.md || '6px'};
  margin-bottom: ${props => props.marginBottom || '15px'};
  animation: ${props => props.$isClosing ? fadeOut : fadeIn} 0.3s ease-in-out;
  
  ${props => {
    switch (props.$variant) {
      case 'success':
        return css`
          background-color: ${({ theme }) => theme.colors.success.light ? `${theme.colors.success.light}20` : '#ecfdf5'};
          border-left: 4px solid ${({ theme }) => theme.colors.success.main || '#10b981'};
          color: ${({ theme }) => theme.colors.success.dark || '#047857'};
        `;
      case 'warning':
        return css`
          background-color: ${({ theme }) => theme.colors.warning.light ? `${theme.colors.warning.light}20` : '#fffbeb'};
          border-left: 4px solid ${({ theme }) => theme.colors.warning.main || '#f59e0b'};
          color: ${({ theme }) => theme.colors.warning.dark || '#b45309'};
        `;
      case 'error':
        return css`
          background-color: ${({ theme }) => theme.colors.error.light ? `${theme.colors.error.light}20` : '#fef2f2'};
          border-left: 4px solid ${({ theme }) => theme.colors.error.main || '#ef4444'};
          color: ${({ theme }) => theme.colors.error.dark || '#b91c1c'};
        `;
      case 'info':
      default:
        return css`
          background-color: ${({ theme }) => theme.colors.info.light ? `${theme.colors.info.light}20` : '#eff6ff'};
          border-left: 4px solid ${({ theme }) => theme.colors.info.main || '#3b82f6'};
          color: ${({ theme }) => theme.colors.info.dark || '#1d4ed8'};
        `;
    }
  }}
`;

const IconContainer = styled.div`
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentContainer = styled.div`
  flex: 1;
`;

const AlertTitle = styled.div`
  font-weight: ${({ theme }) => theme.typography.weights?.semibold || '600'};
  font-size: ${({ theme }) => theme.typography.sizes?.sm || '0.875rem'};
  margin-bottom: ${props => props.hasMessage ? '4px' : '0'};
`;

const AlertMessage = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes?.sm || '0.875rem'};
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-left: 12px;
  color: inherit;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Alert = ({
  variant = 'info',
  title,
  message,
  icon,
  dismissible = false,
  autoClose = false,
  autoCloseTime = 5000,
  onClose,
  marginBottom,
  ...props
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  
  const handleClose = useCallback(() => {
    setIsClosing(true);
    
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) onClose();
    }, 300);
  }, [onClose]);
  
  useEffect(() => {
    let timer;
    
    if (autoClose && isVisible) {
      timer = setTimeout(() => {
        handleClose();
      }, autoCloseTime);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [autoClose, autoCloseTime, isVisible, handleClose]);
  
  if (!isVisible) return null;
  
  const getIcon = () => {
    if (icon) return icon;
    
    switch (variant) {
      case 'success':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        );
      case 'warning':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        );
      case 'error':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        );
      case 'info':
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        );
    }
  };
  
  return (
    <AlertContainer $variant={variant} marginBottom={marginBottom} $isClosing={isClosing}>
      <IconContainer>
        {getIcon()}
      </IconContainer>
      <ContentContainer>
        {title && <AlertTitle hasMessage={!!message}>{title}</AlertTitle>}
        {message && <AlertMessage>{message}</AlertMessage>}
      </ContentContainer>
      {dismissible && (
        <CloseButton onClick={handleClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </CloseButton>
      )}
    </AlertContainer>
  );
};

export default Alert;
